<!--
 * @Description: SchemaLiveType component for selecting live categories
 * @Date: 2025-04-05
-->
<template>
  <div class="schema-live-type">
    <div class="wrap">
      <div class="filter-header">
        <el-input
          v-model="searchQuery"
          placeholder="搜索直播分类"
          prefix-icon="el-icon-search"
          clearable
          size="small"
          @clear="getTypeList"
          @input="debounceSearch"
        ></el-input>
      </div>
      
      <el-alert
        v-if="limit && selectedCategories.length >= limit"
        title="已达到最大选择数量"
        type="warning"
        :closable="false"
        class="limit-alert"
      ></el-alert>
      
      <div v-loading="loading" class="category-content">
        <div class="tabs">
          <div
            v-for="tab in categoryList"
            :key="tab._id"
            class="tab-item"
            :class="[
              { active: currentCategory._id === tab._id },
              { selected: isSelected(tab) }
            ]"
            @click="onChangeCategory(tab)"
          >
            {{ tab.name }}
            <el-tag v-if="isSelected(tab)" size="mini" type="success">已选</el-tag>
          </div>
        </div>
        
        <div class="tab-pane" v-if="currentCategory && currentCategory.child && currentCategory.child.length">
          <el-tag
            v-for="item in currentCategory.child"
            :key="item.id"
            size="small"
            class="tag"
            :class="{ selected: isChildSelected(currentCategory, item) }"
            :type="isChildSelected(currentCategory, item) ? 'success' : 'info'"
            @click="toggleSubCategory(currentCategory, item)"
          >
            {{ item.name }}
          </el-tag>
        </div>
        
        <div v-if="categoryList.length === 0" class="empty-state">
          <i class="el-icon-folder-opened"></i>
          <p>暂无直播分类数据</p>
        </div>
      </div>
      
      <div class="selection-summary" v-if="selectedCategories.length > 0">
        <h4>已选择 {{ selectedCategories.length }} 个分类</h4>
        <div class="selection-list">
          <el-tag
            v-for="(category, index) in selectedCategories"
            :key="index"
            size="medium"
            closable
            @close="removeCategory(category)"
            type="success"
            class="mb10 mr10"
          >
            {{ category.name }}{{ category.selectedChildren && category.selectedChildren.length ? ` (${category.selectedChildren.length})` : '' }}
          </el-tag>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getLiveTypeList } from '@/api/material';
import { debounce } from 'lodash';

export default {
  name: 'SchemaLiveType',
  
  props: {
    modelValue: {
      type: Array,
      default: () => []
    },
    limit: {
      type: Number,
      default: 0
    }
  },
  
  data() {
    return {
      loading: false,
      categoryList: [],
      currentCategory: null,
      selectedCategories: [],
      searchQuery: '',
      debounceSearch: null
    };
  },
  
  created() {
    // 初始化防抖搜索
    this.debounceSearch = debounce(() => {
      this.getTypeList();
    }, 500);
    
    // 加载分类数据
    this.getTypeList();
    
    // 如果有初始值，设置已选择的分类
    if (this.modelValue && this.modelValue.length) {
      this.selectedCategories = this.processInitialSelection(this.modelValue);
    }
  },
  
  methods: {
    // 获取分类列表
    async getTypeList() {
      try {
        this.loading = true;
        
        const params = {
          page: 1,
          pageSize: 100
        };
        
        if (this.searchQuery) {
          params.search = this.searchQuery;
        }
        
        const response = await getLiveTypeList(params);
        
        if (response && (response.status === 10000 || response.status === '10000')) {
          this.categoryList = response.data?.categories || [];
          
          // 如果有分类数据，默认选中第一个
          if (this.categoryList.length > 0 && !this.currentCategory) {
            this.currentCategory = this.categoryList[0];
          }
          
          // 如果当前选中的分类不在新的列表中，重置选择
          if (this.currentCategory && !this.categoryList.find(item => item._id === this.currentCategory._id)) {
            this.currentCategory = this.categoryList[0] || null;
          }
          
          // 更新已选中的分类状态
          this.updateSelectedState();
        } else {
          this.$message.error(response?.message || '获取直播分类失败');
          this.categoryList = [];
        }
      } catch (error) {
        console.error('获取直播分类失败:', error);
        this.$message.error('获取直播分类失败');
        this.categoryList = [];
      } finally {
        this.loading = false;
      }
    },
    
    // 处理初始选择的分类
    processInitialSelection(initialValue) {
      if (!Array.isArray(initialValue)) return [];
      
      return initialValue.map(category => {
        // 确保category是对象并且有必要的属性
        if (!category || typeof category !== 'object') return null;
        
        return {
          _id: category._id,
          name: category.name || '未命名分类',
          selectedChildren: Array.isArray(category.child) ? [...category.child] : []
        };
      }).filter(Boolean); // 过滤掉null值
    },
    
    // 更新选中状态
    updateSelectedState() {
      // 遍历所有分类，标记已选中的状态
      this.categoryList.forEach(category => {
        const selected = this.isSelected(category);
        category.selected = selected;
        
        if (category.child && Array.isArray(category.child)) {
          category.child.forEach(subCategory => {
            subCategory.selected = this.isChildSelected(category, subCategory);
          });
        }
      });
    },
    
    // 切换分类选择
    onChangeCategory(category) {
      this.currentCategory = category;
    },
    
    // 选择分类
    selectCategory(category) {
      if (this.limit && this.selectedCategories.length >= this.limit) {
        this.$message.warning(`最多选择${this.limit}个分类`);
        return;
      }
      
      // 检查是否已选择该分类
      if (!this.isSelected(category)) {
        // 创建新的分类对象，避免引用原对象
        const newCategory = {
          _id: category._id,
          name: category.name,
          selectedChildren: []
        };
        
        // 如果有子分类，默认全选
        if (category.child && category.child.length) {
          newCategory.child = [...category.child];
          newCategory.selectedChildren = [...category.child];
        }
        
        this.selectedCategories.push(newCategory);
      }
    },
    
    // 移除分类
    removeCategory(category) {
      const index = this.selectedCategories.findIndex(item => item._id === category._id);
      if (index !== -1) {
        this.selectedCategories.splice(index, 1);
      }
    },
    
    // 切换子分类选择
    toggleSubCategory(category, subCategory) {
      // 首先确保主分类已选中
      let parentCategory = this.selectedCategories.find(item => item._id === category._id);
      
      if (!parentCategory) {
        // 如果主分类未选中，先添加主分类
        this.selectCategory(category);
        parentCategory = this.selectedCategories.find(item => item._id === category._id);
      }
      
      if (!parentCategory) return;
      
      // 确保selectedChildren存在
      if (!parentCategory.selectedChildren) {
        parentCategory.selectedChildren = [];
      }
      
      // 检查子分类是否已选中
      const subIndex = parentCategory.selectedChildren.findIndex(item => item.id === subCategory.id);
      
      if (subIndex === -1) {
        // 如果未选中，添加子分类
        parentCategory.selectedChildren.push({...subCategory});
      } else {
        // 如果已选中，移除子分类
        parentCategory.selectedChildren.splice(subIndex, 1);
      }
    },
    
    // 检查分类是否已选中
    isSelected(category) {
      return this.selectedCategories.some(item => item._id === category._id);
    },
    
    // 检查子分类是否已选中
    isChildSelected(parentCategory, childCategory) {
      const parent = this.selectedCategories.find(item => item._id === parentCategory._id);
      if (!parent || !parent.selectedChildren) return false;
      
      return parent.selectedChildren.some(item => item.id === childCategory.id);
    },
    
    // 获取所有选中的分类
    getSelectedCategories() {
      return this.selectedCategories.map(category => {
        return {
          _id: category._id,
          name: category.name,
          child: category.selectedChildren || []
        };
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.schema-live-type {
  width: 100%;
  
  .filter-header {
    margin-bottom: 15px;
  }
  
  .limit-alert {
    margin-bottom: 15px;
  }
  
  .category-content {
    min-height: 300px;
    border: 1px solid #ebeef5;
    border-radius: 4px;
    padding: 15px;
  }
  
  .tabs {
    display: flex;
    align-items: flex-start;
    gap: 20px;
    overflow-x: auto;
    padding-bottom: 15px;
    border-bottom: 1px solid #ebeef5;
    margin-bottom: 15px;

    .tab-item {
      padding: 4px 10px;
      white-space: nowrap;
      cursor: pointer;
      position: relative;
      transition: all 0.3s;
      
      &:hover {
        color: #409EFF;
      }

      &.active {
        color: #333333;
        font-weight: 600;
        
        &::after {
          content: '';
          position: absolute;
          bottom: -5px;
          left: 50%;
          transform: translateX(-50%);
          width: 24px;
          height: 2px;
          border-radius: 1px;
          background-color: #409EFF;
        }
      }
      
      &.selected {
        color: #67C23A;
      }
    }

    &::-webkit-scrollbar {
      display: block;
      width: 8px;
      height: 6px;
    }

    &::-webkit-scrollbar-thumb {
      border-radius: 4px;
      background-color: #ddd;
    }

    &::-webkit-scrollbar-track {
      background-color: #f5f5f5;
    }
  }

  .tab-pane {
    min-height: 100px;
    margin-bottom: 15px;

    .tag {
      cursor: pointer;
      transition: all 0.3s;
      margin-right: 10px;
      margin-bottom: 10px;
      
      &.selected {
        background-color: #f0f9eb;
        color: #67C23A;
        border-color: #e1f3d8;
      }
    }
  }
  
  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 200px;
    color: #909399;
    
    i {
      font-size: 48px;
      margin-bottom: 10px;
    }
    
    p {
      font-size: 14px;
    }
  }
  
  .selection-summary {
    margin-top: 20px;
    padding-top: 15px;
    border-top: 1px solid #ebeef5;
    
    h4 {
      margin-top: 0;
      margin-bottom: 10px;
      font-size: 14px;
      font-weight: normal;
    }
    
    .selection-list {
      display: flex;
      flex-wrap: wrap;
    }
  }
  
  .mb10 {
    margin-bottom: 10px;
  }
  
  .mr10 {
    margin-right: 10px;
  }
}
</style> 